import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@appcues/sonar';
import { Account, AccountName, AccountsContainer } from './styled';

/**
 * Normalize accounts to array format
 * Handles both array (from auth SDK) and object (from Redux) formats
 *
 * @param {Array|object} accounts - Accounts as array or object keyed by ID
 * @return {Array} Accounts as array
 */
const normalizeAccounts = accounts => {
  if (!accounts) return [];
  return Array.isArray(accounts) ? accounts : Object.values(accounts);
};

/**
 * Alphabetize accounts with ID fallbacks
 *
 * Borrowed from studio's Accounts page
 *
 * @param {Array|object} accounts - Accounts for user
 * @return {Account[]} Sorted accounts list
 */
const alphabetize = accounts =>
  normalizeAccounts(accounts).sort((first, second) => {
    switch (true) {
      // Compare names if both exist
      case Boolean(first.name && second.name):
        return first.name.localeCompare(second.name);

      // Else prefer the account with a name
      case Boolean(first.name && !second.name):
        return -1;
      case Boolean(!first.name && second.name):
        return 1;

      // Otherwise compare account IDs
      default:
        return first.id.localeCompare(second.id);
    }
  });

/**
 * Presentational component for displaying a list of accounts
 *
 * @param {object} props - Component props
 * @param {Array|object} props.accounts - Accounts as array or object keyed by ID
 * @param {string} props.selected - Currently selected account ID
 * @param {boolean} props.isPending - Whether an account switch is in progress
 * @param {function} props.onSelect - Callback when an account is selected
 */
export function AccountsList({
  accounts = [],
  selected = null,
  isPending = false,
  onSelect,
}) {
  // Sort accounts by name and ID
  const sorted = useMemo(() => {
    return alphabetize(accounts);
  }, [accounts]);

  return (
    <AccountsContainer>
      {sorted.map(({ id, name }) => {
        const isActive = id === selected;
        const isDisabled = isPending || isActive;

        return (
          <Account
            key={id}
            disabled={isDisabled}
            onClick={!isDisabled ? () => onSelect(id) : null}
            $selected={isActive}
          >
            <AccountName>{name || `Account ${id}`}</AccountName>
            {isActive && <Text>Currently in use</Text>}
          </Account>
        );
      })}
    </AccountsContainer>
  );
}

const accountShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
});

AccountsList.propTypes = {
  // Accepts both array (from auth SDK) and object (from Redux) formats
  accounts: PropTypes.oneOfType([
    PropTypes.arrayOf(accountShape),
    PropTypes.objectOf(accountShape),
  ]),
  selected: PropTypes.string,
  isPending: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default AccountsList;
